<template>
  <div class='editInfoPage' id='editInfoPage' ref='editInfoPage'>
    <TopBack :title="$t('lang.common.EditProfile')"/>
    <div class="formBox">
      <div class="avatar-wrap">
        <div class="avatar">{{ getFirstLetter() }}</div>
      </div>
      <div class="form-group">
        <label class="label">{{ $t('lang.editInfo.name') }}</label>
        <div class="input-wrap" :class="{error: !nameValid}">
          <input v-model="userName" type="text" placeholder=""/>
          <div class="clear-btn" v-if="userName" @click="clearName()"><img src="../../assets/images/edit/clear.svg" alt=""/></div>
        </div>
        <div class="error-tip" v-if="!nameValid"><img src="../../assets/images/edit/error.svg" alt=""/>
          <div>{{ $t('lang.editInfo.nameError') }}</div>
        </div>
      </div>

      <div class="form-group">
        <div class="label-row">
          <label class="label">{{ $t('lang.editInfo.uid') }}</label>
          <span class="sub-label">{{ $t('lang.editInfo.permanentIdentifier') }}</span>
        </div>
        <div class="input-wrap readonly">
          <span class="uid-text">{{ userInfo.showId }}</span>
          <div class="copy-btn" @click="cpShowId()">
            <div><img src="../../assets/images/edit/copy.svg" alt="">{{ $t('lang.editInfo.copy') }}</div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="label">{{ $t('lang.editInfo.phoneNumber') }}</label>
        <div class="input-wrap readonly" v-if="userInfo.mobileNo">
          <span class="phone-text">{{ maskedPhone }}</span>
        </div>
        <div class="input-wrap readonly" v-else>
          <div class="phone-text warn"><img src="../../assets/images/edit/warn.svg"/><div>{{ $t('lang.editInfo.bindForSecurity') }}</div></div>
          <div class="bindBtn" @click="goto('BindPhone')">{{ $t('lang.editInfo.bindPhone') }}</div>
        </div>
      </div>
    </div>
    <button class="save-btn" @click="updateUsername()">{{ $t('lang.editInfo.saveChanges') }}</button>
  </div>
</template>

<script>
import {mapMutations} from 'vuex'
import TopBack from '@/components/TopBack/index'
import {Toast} from 'mint-ui'
import request from '@/service'

// 后端只回传手机号后 4 位，前面固定用 5 个圆点占位遮蔽
const PHONE_MASK = '•••••'

export default {
  name: 'EditInfo',
  components: {
    TopBack,
    Toast
  },
  data () {
    return {
      userInfo: JSON.parse(localStorage.getItem('userInfo')),
      userName: '',
      form: {
        name: '',
        uid: '',
        phone: ''
      }
    }
  },
  watch: {
    // 路由变化时重新从 localStorage 更新 userInfo
    '$route' () {
      this.refreshUserInfo()
    }
  },
  computed: {
    // 姓名规则：去除首尾空格后长度为 4-20 个字符。
    // 用 computed 而非 watch，输入时自动重算，进入页面首次渲染也会按同一规则校验。
    nameValid () {
      const len = String(this.userName || '').trim().length
      return len >= 4 && len <= 20
    },
    // 已绑定手机号的展示态：+91 ••••• 8765。取 mobileNo 末 4 位，兼容后端日后回传完整号码的情况。
    maskedPhone () {
      const last4 = String((this.userInfo || {}).mobileNo || '').slice(-4)
      return '+91 ' + PHONE_MASK + ' ' + last4
    }
  },
  mounted () {
    if (!this.userInfo) {
      this.goto('Login')
    }
    this.userName = this.userInfo.username
  },
  activated () {
    // keep-alive 缓存激活时刷新
    this.refreshUserInfo()
  },
  beforeDestroy () {
  },
  methods: {
    ...mapMutations({
      setCurrentRoute: 'setCurrentRoute',
      setInternationName: 'setInternationName',
      setInternation: 'setInternation'
    }),
    // 从 localStorage 刷新 userInfo
    refreshUserInfo () {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'))
      if (userInfo) {
        this.userInfo = userInfo
        this.userName = userInfo.username
      }
    },
    goto (route) {
      this.$router.push(route)
    },
    clearName () {
      this.userName = ''
    },
    cpShowId () {
      const text = this.userInfo.showId
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          Toast('Copied!')
        }).catch(() => {
          this.fallbackCopy(text)
        })
      } else {
        this.fallbackCopy(text)
      }
    },
    fallbackCopy (text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        alert('Copied!')
      } catch (err) {
        alert('Copy failed')
      }
      document.body.removeChild(textarea)
    },
    getFirstLetter () {
      return this.userInfo.username.charAt(0).toUpperCase()
    },
    updateUsername () {
      // 校验用户名
      if (!this.nameValid) {
        Toast(this.$t('lang.editInfo.nameError'))
        return
      }
      const username = String(this.userName || '').trim()
      // 如果没变化，直接返回
      if (username === this.userInfo.username) {
        Toast('No changes')
        return
      }
      // 调用更新接口
      request('post', '/api/user/update/username', { username: username }).then(res => {
        if (res && res.code === 200) {
          Toast(this.$t('lang.common.success'))
          // 更新本地 userInfo
          this.userInfo.username = username
          localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
        } else {
          Toast(res.message || 'Update failed')
        }
      }).catch(() => {
        Toast('Update failed, please try again')
      })
    }
  }
}
</script>

<style lang='less' scoped>
.editInfoPage {
  position: relative;
  padding: calc(60vw / 3.6) 0;
  height: 100dvh;
  overflow: auto;
  background: var(--background);

  .avatar-wrap {
    display: flex;
    justify-content: center;

    .avatar {
      width: calc(80vw / 3.6);
      height: calc(80vw / 3.6);
      border-radius: 50%;
      background: #edf4ff;
      color: var(--footerAct);
      font-size: calc(32vw / 3.6);
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      border: calc(2vw / 3.6) solid var(--footerAct);
    }
  }

  .formBox {
    position: relative;
    left: 50%;
    transform: translate(-50%);
    width: calc(328vw / 3.6);
    height: max-content;
    box-sizing: border-box;
    background: #FFFFFF;
    border: calc(1vw / 3.6) solid rgba(195, 197, 216, 0.3);
    box-shadow: 0 calc(1vw / 3.6) calc(8vw / 3.6) rgba(0, 0, 0, 0.04);
    border-radius: calc(12vw / 3.6);
    margin-top: calc(8vw / 3.6);
    padding: calc(16vw / 3.6);

    .form-group {
      margin-bottom: calc(16vw / 3.6);

      .label {
        display: block;
        font-size: calc(14vw / 3.6);
        margin-bottom: calc(8vw / 3.6);
        font-family: 'Hanken Grotesk';
        font-style: normal;
        font-weight: 600;
        color: var(--blackFont);
      }

      .label-row {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .sub-label {
          font-size: calc(12vw / 3.6);
          color: #888;
        }
      }

      .input-wrap {
        position: relative;
        border: calc(1vw / 3.6) solid #ddd;
        width: calc(296vw / 3.6);
        height: calc(48vw / 3.6);
        line-height: calc(48vw / 3.6);
        border-radius: calc(12vw / 3.6);
        padding: 0 12vw 0 calc(14vw / 3.6);
        background: #f7f8fa;
        display: flex;
        align-items: center;

        .warn {
          font-weight: 500;
          font-size: calc(14vw / 3.6);
          color: var(--err);
          display: flex;
          align-items: center;
          justify-content: flex-start;

          img {
            width: calc(18vw / 3.6);
            height: auto;
            margin-right: 1vw;
          }
        }

        .bindBtn {
          position: absolute;
          right: calc(12vw / 3.6);
          width: calc(85vw / 3.6);
          height: calc(28vw / 3.6);
          border-radius: calc(8vw / 3.6);
          line-height: calc(28vw / 3.6);
          background: var(--footerAct);
          text-align: center;
          font-size: calc(12vw / 3.6);
          color: var(--white);
        }

        &.error {
          border-color: var(--err);
          background: #fff;

          input {
            border: none;
            outline: none;
            flex: 1;
            font-size: calc(16vw / 3.6);
          }
        }

        &.readonly {
          background: #eef1f7;
          color: #555;
          border: none;
        }

        input {
          flex: 1;
          border: none;
          outline: none;
          font-size: calc(16vw / 3.6);
          background: transparent;
        }

        .clear-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          right: calc(12vw / 3.6);
          width: calc(15vw / 3.6);
          height: calc(15vw / 3.6);
          border-radius: 50%;
          color: #333;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: calc(14vw / 3.6);
        }

        .copy-btn {
          position: absolute;
          right: calc(12vw / 3.6);
          color: var(--footerAct);
          font-size: calc(14vw / 3.6);
          display: flex;
          align-items: center;
          gap: calc(4vw / 3.6);

          img {
            margin-right: 0.5vw;
          }
        }
      }

      .error-tip {
        color: var(--err);
        font-size: calc(13vw / 3.6);
        margin-top: calc(6vw / 3.6);
        display: flex;
        align-items: flex-start;
        gap: calc(4vw / 3.6);
      }
    }
  }

  .save-btn {
    position: relative;
    left: 50%;
    transform: translate(-50%);
    width: calc(328vw / 3.6);
    height: calc(48vw / 3.6);
    border-radius: calc(12vw / 3.6);
    background: var(--footerAct);
    color: var(--white);
    border: none;
    font-size: calc(14vw / 3.6);
    font-weight: 600;
    margin-top: calc(24vw / 3.6);
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/EditInfo/index.vue